import React, { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

/**
 * Reusable tag input for target_products (multi-select).
 * Supports adding tags via Enter or comma, removing via X on badge.
 */
export default function TargetProductsTagInput({ value = [], onChange, placeholder = 'Add product key...', disabled }) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const tags = Array.isArray(value) ? value.filter(Boolean) : [];

  const addTag = (tag) => {
    const trimmed = String(tag).trim().toUpperCase();
    if (!trimmed || tags.includes(trimmed)) return;
    onChange([...tags, trimmed]);
    setInputValue('');
  };

  const removeTag = (index) => {
    const next = tags.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) addTag(inputValue);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 min-h-[38px] px-3 py-2 border rounded-md bg-white focus-within:ring-2 focus-within:ring-[#00C2C2] focus-within:border-transparent"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag, i) => (
        <Badge
          key={i}
          variant="secondary"
          className="bg-[#00C2C2]/10 text-[#00A3A3] border border-[#00C2C2]/30 px-2 py-0.5 text-xs font-medium flex items-center gap-1"
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(i); }}
              className="hover:bg-[#00C2C2]/20 rounded p-0.5"
              aria-label={`Remove ${tag}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      ))}
      {!disabled && (
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] border-0 p-0 h-7 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      )}
    </div>
  );
}
